import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE as API } from '../../lib/config';
import {
  ChevronLeftIcon,
  TrophyIcon,
  StarIcon,
  FireIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  HeartIcon,
  ClockIcon,
  CurrencyYenIcon
} from '@heroicons/react/24/outline';
import { TrophyIcon as TrophyIconSolid } from '@heroicons/react/24/solid';

const Badges = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0); // 0-indexed
  const pageSize = 6;

  // Default 6 badges (labels/order aligned to design)
  const DEFAULTS = [
    { id: 'registration-completed', name: '登録完了', description: '会員登録が完了しました', icon: 'star', color: 'blue', current: 0, target: 1, progress: 0, earned: false },
    { id: 'identity-verified', name: '本人確認完了', description: '本人確認が完了しました', icon: 'trophy', color: 'yellow', current: 0, target: 1, progress: 0, earned: false },
    { id: 'favorites-count', name: 'お気に入り回数', description: '求人のお気に入り数', icon: 'heart', color: 'pink', current: 0, target: 5, progress: 0, earned: false },
    { id: 'first-job', name: 'はじめての仕事', description: '初回のお仕事を完了しました', icon: 'star', color: 'green', current: 0, target: 1, progress: 0, earned: false },
    { id: 'jobs-in-period', name: '期間中の仕事回数', description: '直近30日での仕事完了回数', icon: 'clock', color: 'purple', current: 0, target: 1, progress: 0, earned: false },
    { id: 'stores-worked', name: '働いた店舗の数', description: '勤務した店舗/事業者の数', icon: 'shield', color: 'red', current: 0, target: 2, progress: 0, earned: false },
  ];

  useEffect(() => {
    fetchBadges();
  }, []);

  const isForceAll = () => {
    try {
      const hasWindow = typeof window !== 'undefined';
      if (!hasWindow) return false;
      const params = new URLSearchParams(window.location.search);
      const q = params.get('all_badges');
      const ls = window.localStorage.getItem('force_all_badges');
      const host = window.location.hostname || '';
      const isLocalHost = host === 'localhost' || host === '127.0.0.1';
      const envForce = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_FORCE_ALL_BADGES === '1');
      return q === '1' || ls === '1' || isLocalHost || envForce;
    } catch (_) {
      return false;
    }
  };

  const forceEarnAll = (items) => {
    return (items || []).map(b => ({
      ...b,
      earned: true,
      current: b.target || 1,
      progress: 100,
    }));
  };

  const fetchBadges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/user/badges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        // Fallback to defaults on error
        const d = isForceAll() ? forceEarnAll(DEFAULTS) : DEFAULTS;
        setBadges(d);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      // Merge into defaults to guarantee 6 slots
      const map = Object.fromEntries(DEFAULTS.map(d => [d.id, { ...d }]));
      for (const b of list) {
        if (b && b.id && map[b.id]) {
          map[b.id] = { ...map[b.id], ...b };
        }
      }
      const merged = Object.values(map);
      setBadges(isForceAll() ? forceEarnAll(merged) : merged);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
      const d = isForceAll() ? forceEarnAll(DEFAULTS) : DEFAULTS;
      setBadges(d);
    } finally {
      setLoading(false);
    }
  };

  const getIconComponent = (iconType) => {
    switch (iconType) {
      case 'star': return StarIcon;
      case 'shield': return ShieldCheckIcon;
      case 'clock': return ClockIcon;
      case 'currency': return CurrencyYenIcon;
      case 'fire': return FireIcon;
      case 'heart': return HeartIcon;
      case 'trophy': return TrophyIcon;
      default: return TrophyIcon;
    }
  };

  const getColorClasses = (color, earned) => {
    const baseClasses = {
      blue: earned ? 'bg-blue-100 text-blue-600 border-blue-200' : 'bg-gray-100 text-gray-400 border-gray-200',
      green: earned ? 'bg-green-100 text-green-600 border-green-200' : 'bg-gray-100 text-gray-400 border-gray-200',
      purple: earned ? 'bg-purple-100 text-purple-600 border-purple-200' : 'bg-gray-100 text-gray-400 border-gray-200',
      yellow: earned ? 'bg-yellow-100 text-yellow-600 border-yellow-200' : 'bg-gray-100 text-gray-400 border-gray-200',
      red: earned ? 'bg-red-100 text-red-600 border-red-200' : 'bg-gray-100 text-gray-400 border-gray-200',
      pink: earned ? 'bg-pink-100 text-pink-600 border-pink-200' : 'bg-gray-100 text-gray-400 border-gray-200'
    };
    return baseClasses[color] || baseClasses.blue;
  };

  const totalPages = Math.max(1, Math.ceil((badges?.length || 0) / pageSize));
  const start = page * pageSize;
  const currentPageBadges = (badges?.length ? badges : DEFAULTS).slice(start, start + pageSize);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">バッジ一覧</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ページネーションとバッジグリッド */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6" style={{ visibility: totalPages > 1 ? 'visible' : 'hidden' }}>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              disabled={page === 0}
            >
              ← 前へ
            </button>
            <div className="text-sm text-gray-600">{page + 1} / {totalPages}</div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              disabled={page >= totalPages - 1}
            >
              次へ →
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {currentPageBadges.map((badge) => {
              const IconComponent = getIconComponent(badge.icon);
              const baseProgress = Math.min(100, Math.max(0, badge.progress ?? (badge.target ? Math.round(((badge.current || 0) / badge.target) * 100) : 0)));
              const isProdBuild = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production');
              const earned = isProdBuild ? !!badge.earned : (!!badge.earned || baseProgress >= 100);
              const progressPct = earned ? 100 : baseProgress;
              return (
                <div key={badge.id} className="flex flex-col items-center">
                  {/* Coin base */}
                  <div className={`relative w-20 h-20 rounded-full border shadow-inner flex items-center justify-center overflow-hidden ${earned ? 'bg-gradient-to-b from-yellow-200 to-yellow-400 border-yellow-500 ring-2 ring-yellow-300/60' : 'bg-gradient-to-b from-gray-100 to-gray-200 border-gray-300'}`}>
                    <IconComponent className={`w-10 h-10 ${earned ? 'text-yellow-600' : 'text-gray-400'} ${earned ? '' : 'filter grayscale opacity-70'}`} />
                  </div>
                  <div className="mt-2 text-sm text-gray-900 font-medium text-center">{badge.name}</div>
                  <div className="mt-2 w-full">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${earned ? 'bg-yellow-500' : 'bg-yellow-400'}`} style={{ width: `${progressPct}%` }}></div>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-gray-600 text-center">
                    {earned ? '達成！' : `${badge.current || 0}/${badge.target || 1}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* バッジについて */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <TrophyIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">バッジについて</h4>
              <p className="text-blue-800 text-sm leading-relaxed">
                バッジは、バイターズでの活動実績を表彰するシステムです。
                お仕事を頑張ったり、高い評価を獲得したりすることで様々なバッジを獲得できます。
                バッジを集めて、あなたの実力をアピールしましょう！
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Badges;